import { fetchData } from "services/api/fetchData";

/*
 * pollPdfRender.ts
 * Shared helper that starts a pdf render on the backend worker,
 * polls until the render is finished and downloads the resulting
 * pdf as a blob through the browser download flow.
 */

interface EnqueueAndDownloadPdfProps {
    enqueueUrl: string;
    fileName: string;
    handleLogout: (options?: { logoutFromServer?: boolean, showSessionAlert?: boolean }) => Promise<void>;
}

// Total time in milliseconds before the polling gives up
const POLL_TIMEOUT_MS = 60000;

// Poll quickly at first and slow down after the first few polls
const INITIAL_POLL_INTERVAL_MS = 250;
const INITIAL_POLL_COUNT = 2;
const FAST_POLL_INTERVAL_MS = 500;
const FAST_POLL_COUNT = 6;
const SLOW_POLL_INTERVAL_MS = 1000;

export const enqueueAndDownloadPdf = async ({ enqueueUrl, fileName, handleLogout }: EnqueueAndDownloadPdfProps) => {

    // Start the pdf render on the backend worker
    const enqueueResponse = await fetchData({ apiUrl: enqueueUrl, handleLogout });
    const renderId = enqueueResponse?.render_id;

    // Throw when the render could not be started
    if (!renderId) {
        throw new Error('Pdf render could not be started');
    }

    // Poll the render status until the pdf is finished
    const startTime = Date.now();
    let pollCount = 0;
    while (true) {

        // Give up when the render takes longer than the timeout
        if (Date.now() - startTime > POLL_TIMEOUT_MS) {
            throw new Error('Pdf render timed out');
        }

        // Wait before the next poll, quickly at first and slower after a few polls
        const pollInterval = pollCount < INITIAL_POLL_COUNT
            ? INITIAL_POLL_INTERVAL_MS
            : pollCount < FAST_POLL_COUNT
                ? FAST_POLL_INTERVAL_MS
                : SLOW_POLL_INTERVAL_MS;
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        pollCount += 1;

        // Get the current render status from the backend
        const statusResponse = await fetchData({ apiUrl: `pdf_render_status/${renderId}`, handleLogout });

        // Stop polling when the render is finished
        if (statusResponse?.status === 'finished') {
            break;
        }

        // Throw when the render failed on the worker
        if (statusResponse?.status === 'failed') {
            throw new Error('Pdf render failed');
        }
    }

    // Download the finished pdf as a blob
    const blob = await fetchData({
        apiUrl: `pdf_render_file/${renderId}`,
        responseAsBlob: true,
        handleLogout,
    });

    // Create a link to download the pdf
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
